/**
 * SADARayon — Pilotage (P1) · Rapport admin/manager des actions attendues.
 *
 * Fonctionnalités :
 *   - Tableau des actions attendues par inventaire (Date, Rayon, Étape, Personne, Rôle, Retard, Dernier commentaire blocage)
 *   - Filtres : période, étape, rayon, agent, retard uniquement, seuil de retard
 *   - Exports PDF et Excel (avec les mêmes filtres)
 *   - Section "Groupé par agent" toujours visible avec bouton "Relancer via WhatsApp"
 *
 * Backend : routers/inventory_pilotage.py — endpoints /api/inventory/pending-actions[...]
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  AlertTriangle, RefreshCw, FileSpreadsheet, FileText, MessageCircle,
  Filter, Clock, Users, ListChecks, Loader2, Bell, BellOff, Send,
} from 'lucide-react';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Badge } from '../../ui/badge';
import { useSadaRayonAlerts } from '../../../hooks/useSadaRayonAlerts';
import { playLateAlert } from '../../../utils/soundFeedback';
import InventoryDevicesAdmin from './InventoryDevicesAdmin';
import InventoryConflictsSection from './InventoryConflictsSection';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const auth = () => {
  const t = localStorage.getItem('dermascan_token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const STEP_OPTIONS = [
  { id: '', label: 'Toutes les étapes' },
  { id: 'not_started', label: 'Non démarré' },
  { id: 'op1_in_progress', label: 'OP1 en cours' },
  { id: 'awaiting_op2', label: 'Attente OP2' },
  { id: 'op2_in_progress', label: 'OP2 en cours' },
  { id: 'awaiting_close', label: 'Attente clôture' },
];

const STEP_TONES = {
  not_started: 'bg-zinc-100 text-zinc-700 border-zinc-200',
  op1_in_progress: 'bg-blue-100 text-blue-700 border-blue-200',
  awaiting_op2: 'bg-amber-100 text-amber-800 border-amber-200',
  op2_in_progress: 'bg-indigo-100 text-indigo-700 border-indigo-200',
  awaiting_close: 'bg-purple-100 text-purple-700 border-purple-200',
  closed: 'bg-emerald-100 text-emerald-700 border-emerald-200',
};

const formatDelay = (minutes) => {
  const m = Math.max(0, Math.floor(minutes || 0));
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60);
  const r = m % 60;
  return `${h}h${r.toString().padStart(2, '0')}`;
};

const formatDate = (iso) => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
  } catch {
    return String(iso).slice(0, 10);
  }
};

const formatDateTime = (iso) => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
  } catch {
    return iso;
  }
};

// Normalise un numéro CI (préfixe 225 si 10 chiffres locaux)
const normalizePhone = (raw) => {
  if (!raw) return '';
  const digits = String(raw).replace(/\D/g, '');
  if (digits.startsWith('225')) return digits;
  if (digits.length === 10) return `225${digits}`;
  return digits;
};

// Affiche les 3 acteurs assignés (OP1 · OP2 · Responsable) en surlignant
// celui qui est actuellement attendu pour faire avancer l'inventaire.
const TeamCell = ({ row }) => {
  const activeRole = (row.expected_role || '').toLowerCase();
  const entries = [
    { role: 'OP1', name: row.op1_name, active: activeRole === 'op1' },
    { role: 'OP2', name: row.op2_name, active: activeRole === 'op2' },
    { role: 'Responsable', name: row.responsable_name, active: activeRole === 'responsable' },
  ];
  return (
    <ul className="space-y-0.5 text-xs">
      {entries.map((e) => (
        <li
          key={e.role}
          className={`flex items-baseline gap-1.5 ${
            e.active ? 'text-emerald-700 font-semibold' : 'text-zinc-600'
          }`}
        >
          <span className="inline-block w-[78px] text-[10px] uppercase tracking-wide opacity-70">
            {e.role}
          </span>
          <span className="truncate">{e.name || <span className="italic text-zinc-400">Non assigné</span>}</span>
          {e.active && <span className="text-emerald-600 ml-1" title="Étape en cours">●</span>}
        </li>
      ))}
    </ul>
  );
};

const InventoryPilotageReport = ({ zones = [], agents = [] }) => {
  const today = new Date().toISOString().slice(0, 10);
  const thirtyDaysAgo = new Date(Date.now() - 30 * 24 * 3600 * 1000).toISOString().slice(0, 10);

  const [filters, setFilters] = useState({
    from_date: thirtyDaysAgo,
    to_date: today,
    agent_id: '',
    zone_id: '',
    step: '',
    only_late: true,
    late_threshold_minutes: 120,
  });

  const [data, setData] = useState({ count: 0, rows: [] });
  const [grouped, setGrouped] = useState({ groups: [], total_count: 0 });
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(null); // 'pdf' | 'excel' | null

  // Push notifications & in-app sound alerts
  const alerts = useSadaRayonAlerts();
  const knownLateIdsRef = useRef(new Set());
  const isFirstLoadRef = useRef(true);

  const buildParams = useCallback((extra = {}) => {
    const p = {};
    if (filters.from_date) p.from_date = filters.from_date;
    if (filters.to_date) p.to_date = filters.to_date;
    if (filters.agent_id) p.agent_id = filters.agent_id;
    if (filters.zone_id) p.zone_id = filters.zone_id;
    if (filters.step) p.step = filters.step;
    if (filters.only_late) p.only_late = true;
    if (filters.late_threshold_minutes) p.late_threshold_minutes = filters.late_threshold_minutes;
    return { ...p, ...extra };
  }, [filters]);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const params = buildParams();
      const [main, byAgent] = await Promise.all([
        axios.get(`${API}/inventory/pending-actions`, { headers: auth(), params }),
        axios.get(`${API}/inventory/pending-actions/by-agent`, {
          headers: auth(),
          params: {
            from_date: params.from_date,
            to_date: params.to_date,
            only_late: params.only_late || false,
            late_threshold_minutes: params.late_threshold_minutes,
          },
        }),
      ]);
      setData(main.data);
      setGrouped(byAgent.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur chargement Pilotage');
    } finally {
      setLoading(false);
    }
  }, [buildParams]);

  useEffect(() => { reload(); }, [reload]);

  // In-app sound + vibration on new late inventory (every 60s)
  // Even if the user is just looking at the page, a fresh late item triggers
  // an audible bip + vibration on top of the system push notification.
  useEffect(() => {
    const lateIds = new Set(
      (data.rows || [])
        .filter((r) => r.is_late && r.inventory_id)
        .map((r) => r.inventory_id)
    );
    if (isFirstLoadRef.current) {
      isFirstLoadRef.current = false;
      knownLateIdsRef.current = lateIds;
      return;
    }
    const newLate = [...lateIds].filter((id) => !knownLateIdsRef.current.has(id));
    if (newLate.length > 0) {
      try { playLateAlert(); } catch { /* ignore */ }
      toast.warning(
        `${newLate.length} nouveau(x) retard(s) détecté(s) — voir la liste ci-dessous.`,
        { duration: 6000 }
      );
    }
    knownLateIdsRef.current = lateIds;
  }, [data.rows]);

  useEffect(() => {
    const id = setInterval(() => { reload(); }, 60000);
    return () => clearInterval(id);
  }, [reload]);

  const onExport = async (kind) => {
    setExporting(kind);
    try {
      const res = await axios.get(`${API}/inventory/pending-actions/${kind}`, {
        headers: auth(),
        params: buildParams(),
        responseType: 'blob',
      });
      const ext = kind === 'pdf' ? 'pdf' : 'xlsx';
      const mime = kind === 'pdf'
        ? 'application/pdf'
        : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
      const url = window.URL.createObjectURL(new Blob([res.data], { type: mime }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `sadarayon-pilotage-${today}.${ext}`);
      document.body.appendChild(link); link.click(); link.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 2000);
      toast.success(`Export ${kind.toUpperCase()} généré`);
    } catch (e) {
      toast.error(e.response?.data?.detail || `Erreur export ${kind}`);
    } finally {
      setExporting(null);
    }
  };

  const onRelaunchWhatsApp = (group) => {
    const agent = agents.find((a) => a.id === group.agent_id);
    const phone = normalizePhone(agent?.phone);
    const text = encodeURIComponent(group.whatsapp_message || '');
    const url = phone
      ? `https://wa.me/${phone}?text=${text}`
      : `https://wa.me/?text=${text}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const stats = useMemo(() => {
    const total = data.rows.length;
    const lateCount = data.rows.filter((r) => r.is_late).length;
    const blocked = data.rows.filter((r) => r.last_blocking_comment).length;
    return { total, lateCount, blocked };
  }, [data.rows]);

  return (
    <div className="space-y-4" data-testid="inv-pilotage">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-serif font-semibold text-zinc-900">Pilotage SADARayon</h2>
          <p className="text-sm text-zinc-500">Synthèse des actions attendues, retards et blocages</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline" size="sm"
            onClick={() => onExport('excel')}
            disabled={!!exporting || loading}
            data-testid="pilotage-export-excel"
          >
            {exporting === 'excel'
              ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              : <FileSpreadsheet className="w-4 h-4 mr-2" />}
            Excel
          </Button>
          <Button
            variant="outline" size="sm"
            onClick={() => onExport('pdf')}
            disabled={!!exporting || loading}
            data-testid="pilotage-export-pdf"
          >
            {exporting === 'pdf'
              ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              : <FileText className="w-4 h-4 mr-2" />}
            PDF
          </Button>
          <Button
            size="sm"
            onClick={reload}
            disabled={loading}
            className="bg-[#1B4D3E] hover:bg-[#1B4D3E]/90 text-white"
            data-testid="pilotage-refresh"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Actualiser
          </Button>
        </div>
      </div>

      {/* KPI tiles */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div className="rounded-lg border border-zinc-200 bg-white p-4" data-testid="pilotage-kpi-total">
          <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-zinc-500 font-semibold">
            <ListChecks className="w-3.5 h-3.5" /> Actions attendues
          </div>
          <p className="text-2xl font-mono font-bold text-zinc-900 mt-1 tabular-nums">{stats.total}</p>
        </div>
        <div className="rounded-lg border border-red-200 bg-red-50 p-4" data-testid="pilotage-kpi-late">
          <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-red-700 font-semibold">
            <AlertTriangle className="w-3.5 h-3.5" /> Retards
          </div>
          <p className="text-2xl font-mono font-bold text-red-800 mt-1 tabular-nums">{stats.lateCount}</p>
          <p className="text-xs text-red-700 mt-0.5">
            seuil : {Math.floor(filters.late_threshold_minutes / 60)}h{(filters.late_threshold_minutes % 60).toString().padStart(2, '0')}
          </p>
        </div>
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-4" data-testid="pilotage-kpi-blocked">
          <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-amber-700 font-semibold">
            <Clock className="w-3.5 h-3.5" /> Avec commentaire blocage
          </div>
          <p className="text-2xl font-mono font-bold text-amber-800 mt-1 tabular-nums">{stats.blocked}</p>
        </div>
      </div>

      {/* Alertes push & son in-app */}
      <div
        className="rounded-lg border border-zinc-200 bg-white p-4 flex items-center justify-between flex-wrap gap-3"
        data-testid="pilotage-alerts-card"
      >
        <div className="flex items-start gap-3">
          {alerts.isSubscribed ? (
            <Bell className="w-5 h-5 text-emerald-600 mt-0.5" />
          ) : (
            <BellOff className="w-5 h-5 text-zinc-400 mt-0.5" />
          )}
          <div>
            <p className="font-medium text-zinc-900">
              {alerts.isSubscribed
                ? 'Alertes push activées sur cet appareil'
                : 'Alertes push non activées sur cet appareil'}
            </p>
            <p className="text-xs text-zinc-500 mt-0.5">
              {alerts.isSupported
                ? 'Reçoit une notification sur le téléphone dès qu\'un inventaire dépasse 2h sans avancement. Pas de notif entre 21h et 7h, ni le dimanche.'
                : 'Notifications push non supportées par ce navigateur — bip et vibration in-app uniquement.'}
            </p>
            {alerts.iosNeedsInstall && !alerts.isSubscribed && (
              <div
                className="mt-2 rounded-md bg-amber-50 border border-amber-200 p-2.5 text-xs text-amber-900 max-w-md"
                data-testid="pilotage-alerts-ios-hint"
              >
                <p className="font-semibold mb-1">📱 Sur iPhone, étape obligatoire :</p>
                <ol className="list-decimal pl-4 space-y-0.5">
                  <li>Ouvre <strong>Safari</strong> sur <code>phciesada.org</code></li>
                  <li>Tape sur l'icône <strong>Partager</strong> (carré + flèche)</li>
                  <li>Choisis <strong>« Sur l'écran d'accueil »</strong></li>
                  <li>Ouvre l'app depuis la nouvelle icône → reviens ici → réactive</li>
                </ol>
                <p className="mt-1.5 italic">Apple bloque les notifs push tant que l'app n'est pas installée sur l'écran d'accueil.</p>
              </div>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {alerts.isSubscribed ? (
            <>
              <Button
                size="sm" variant="outline"
                onClick={alerts.sendTest}
                disabled={alerts.loading}
                data-testid="pilotage-alerts-test"
              >
                <Send className="w-3.5 h-3.5 mr-1.5" />
                Tester
              </Button>
              <Button
                size="sm" variant="outline"
                onClick={alerts.unsubscribe}
                disabled={alerts.loading}
                className="border-red-200 text-red-700 hover:bg-red-50"
                data-testid="pilotage-alerts-unsubscribe"
              >
                <BellOff className="w-3.5 h-3.5 mr-1.5" />
                Désactiver
              </Button>
            </>
          ) : (
            <Button
              size="sm"
              onClick={alerts.subscribe}
              disabled={alerts.loading || !alerts.isSupported}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="pilotage-alerts-subscribe"
            >
              {alerts.loading
                ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                : <Bell className="w-3.5 h-3.5 mr-1.5" />}
              Activer les alertes sur ce téléphone
            </Button>
          )}
        </div>
      </div>

      {/* Diagnostic technique des alertes (debug iPhone) */}
      <details className="rounded-lg border border-zinc-200 bg-white px-4 py-2 text-xs" data-testid="pilotage-alerts-diag">
        <summary className="cursor-pointer text-zinc-500 hover:text-zinc-800 select-none">
          🛠 Diagnostic technique des alertes (clic pour ouvrir)
        </summary>
        <div className="mt-3 grid grid-cols-2 gap-2 text-zinc-600 font-mono">
          <div>Permission : <span className={alerts.diagnostic.permission === 'granted' ? 'text-emerald-700 font-bold' : 'text-red-700 font-bold'}>{alerts.diagnostic.permission}</span></div>
          <div>Abonné : <span className="font-bold">{String(alerts.diagnostic.isSubscribed)}</span></div>
          <div>Supporté : <span className="font-bold">{String(alerts.diagnostic.isSupported)}</span></div>
          <div>PWA installée : <span className="font-bold">{String(alerts.diagnostic.standalone)}</span></div>
          <div>iOS besoin install : <span className="font-bold">{String(alerts.diagnostic.iosNeedsInstall)}</span></div>
          <div>Service Worker API : <span className="font-bold">{String(alerts.diagnostic.hasServiceWorker)}</span></div>
          <div>PushManager API : <span className="font-bold">{String(alerts.diagnostic.hasPushManager)}</span></div>
          <div>Notification API : <span className="font-bold">{String(alerts.diagnostic.hasNotification)}</span></div>
          <div className="col-span-2 text-zinc-400 text-[10px] break-all">UA : {alerts.diagnostic.ua}</div>
        </div>
        <p className="mt-3 text-zinc-500 italic">
          💡 Si Permission = denied sur iPhone : Réglages iOS → Notifications → Phcie SADA → autoriser.
          Si l&apos;app n&apos;apparaît pas dans la liste, supprime puis réinstalle depuis Safari (Partager → Sur l&apos;écran d&apos;accueil).
        </p>
      </details>

      {/* Filtres */}
      <div className="rounded-lg border border-zinc-200 bg-white p-4">
        <div className="flex items-center gap-2 mb-3 text-xs uppercase tracking-wide text-zinc-500 font-semibold">
          <Filter className="w-3.5 h-3.5" /> Filtres
        </div>
        <div className="grid grid-cols-2 md:grid-cols-6 gap-2">
          <div>
            <label className="block text-xs text-zinc-500 mb-1">Du</label>
            <Input
              type="date"
              value={filters.from_date}
              onChange={(e) => setFilters((f) => ({ ...f, from_date: e.target.value }))}
              data-testid="pilotage-from-date"
            />
          </div>
          <div>
            <label className="block text-xs text-zinc-500 mb-1">Au</label>
            <Input
              type="date"
              value={filters.to_date}
              onChange={(e) => setFilters((f) => ({ ...f, to_date: e.target.value }))}
              data-testid="pilotage-to-date"
            />
          </div>
          <div>
            <label className="block text-xs text-zinc-500 mb-1">Étape</label>
            <select
              className="w-full border border-zinc-300 rounded-md p-2 text-sm h-10"
              value={filters.step}
              onChange={(e) => setFilters((f) => ({ ...f, step: e.target.value }))}
              data-testid="pilotage-step-filter"
            >
              {STEP_OPTIONS.map((o) => (
                <option key={o.id || 'all'} value={o.id}>{o.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs text-zinc-500 mb-1">Rayon</label>
            <select
              className="w-full border border-zinc-300 rounded-md p-2 text-sm h-10"
              value={filters.zone_id}
              onChange={(e) => setFilters((f) => ({ ...f, zone_id: e.target.value }))}
              data-testid="pilotage-zone-filter"
            >
              <option value="">Tous</option>
              {zones.map((z) => (
                <option key={z.id} value={z.id}>{z.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs text-zinc-500 mb-1">Agent</label>
            <select
              className="w-full border border-zinc-300 rounded-md p-2 text-sm h-10"
              value={filters.agent_id}
              onChange={(e) => setFilters((f) => ({ ...f, agent_id: e.target.value }))}
              data-testid="pilotage-agent-filter"
            >
              <option value="">Tous</option>
              {agents.map((a) => (
                <option key={a.id} value={a.id}>{a.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs text-zinc-500 mb-1">Seuil retard (min)</label>
            <Input
              type="number" min={0} step={30}
              value={filters.late_threshold_minutes}
              onChange={(e) => setFilters((f) => ({ ...f, late_threshold_minutes: parseInt(e.target.value || '0', 10) }))}
              data-testid="pilotage-threshold"
            />
          </div>
        </div>
        <div className="mt-3 flex items-center justify-between">
          <label className="inline-flex items-center gap-2 text-sm text-zinc-700 cursor-pointer">
            <input
              type="checkbox"
              checked={filters.only_late}
              onChange={(e) => setFilters((f) => ({ ...f, only_late: e.target.checked }))}
              data-testid="pilotage-only-late"
            />
            Afficher uniquement les retards
          </label>
          <p className="text-xs text-zinc-500">Modifier un filtre déclenche le rechargement automatiquement.</p>
        </div>
      </div>

      {/* Tableau des actions attendues */}
      <div className="rounded-lg border border-zinc-200 bg-white overflow-hidden">
        <div className="px-4 py-3 border-b border-zinc-200 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <ListChecks className="w-4 h-4 text-[#1B4D3E]" />
            <h3 className="font-medium text-zinc-900">Actions attendues</h3>
            <Badge className="bg-zinc-100 text-zinc-700 border border-zinc-200">{data.rows.length}</Badge>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-zinc-50 border-b border-zinc-200">
              <tr className="text-left text-zinc-600">
                <th className="px-3 py-2 font-medium">Date</th>
                <th className="px-3 py-2 font-medium">Rayon</th>
                <th className="px-3 py-2 font-medium">Étape</th>
                <th className="px-3 py-2 font-medium">Personne attendue</th>
                <th className="px-3 py-2 font-medium">Action</th>
                <th className="px-3 py-2 font-medium">Équipe assignée</th>
                <th className="px-3 py-2 font-medium">Retard</th>
                <th className="px-3 py-2 font-medium">Dernier commentaire</th>
              </tr>
            </thead>
            <tbody data-testid="pilotage-rows">
              {data.rows.map((r) => (
                <tr
                  key={r.inventory_id}
                  className={`border-b border-zinc-100 hover:bg-zinc-50 ${r.is_late ? 'bg-red-50/40' : ''}`}
                  data-testid={`pilotage-row-${r.inventory_id}`}
                >
                  <td className="px-3 py-2 font-mono text-zinc-700 whitespace-nowrap">{formatDate(r.date)}</td>
                  <td className="px-3 py-2 font-medium text-zinc-900">{r.zone_name || '—'}</td>
                  <td className="px-3 py-2">
                    <Badge className={`${STEP_TONES[r.step] || STEP_TONES.not_started} border`}>
                      {r.step_label}
                    </Badge>
                  </td>
                  <td className="px-3 py-2 text-zinc-800">
                    <div>{r.expected_person_name || <span className="text-zinc-400">Non assigné</span>}</div>
                    {r.expected_role && (
                      <div className="text-[11px] uppercase tracking-wide text-zinc-500 mt-0.5">{r.expected_role}</div>
                    )}
                  </td>
                  <td className="px-3 py-2 text-zinc-700">{r.expected_action}</td>
                  <td className="px-3 py-2">
                    <TeamCell row={r} />
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap">
                    {r.is_late ? (
                      <span className="inline-flex items-center gap-1 text-red-700 font-medium">
                        <AlertTriangle className="w-3.5 h-3.5" /> {formatDelay(r.delay_minutes)}
                      </span>
                    ) : (
                      <span className="text-zinc-500">{formatDelay(r.delay_minutes)}</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-zinc-700 max-w-xs">
                    {r.last_blocking_comment ? (
                      <div className="text-xs">
                        <div className="font-medium text-amber-800">
                          [{r.last_blocking_comment.category}] {r.last_blocking_comment.by_name}
                        </div>
                        <div className="text-zinc-600 line-clamp-2">{r.last_blocking_comment.comment}</div>
                        <div className="text-zinc-400 mt-0.5">{formatDateTime(r.last_blocking_comment.at)}</div>
                      </div>
                    ) : <span className="text-zinc-400">—</span>}
                  </td>
                </tr>
              ))}
              {!data.rows.length && (
                <tr>
                  <td colSpan={8} className="px-3 py-6 text-center text-zinc-500" data-testid="pilotage-empty">
                    {loading ? 'Chargement…' : 'Aucune action en attente sur la période sélectionnée.'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Vue groupée par agent (toujours visible) */}
      <div className="rounded-lg border border-zinc-200 bg-white overflow-hidden" data-testid="pilotage-by-agent">
        <div className="px-4 py-3 border-b border-zinc-200 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Users className="w-4 h-4 text-[#1B4D3E]" />
            <h3 className="font-medium text-zinc-900">Groupé par agent — relance WhatsApp</h3>
            <Badge className="bg-zinc-100 text-zinc-700 border border-zinc-200">{grouped.groups?.length || 0}</Badge>
          </div>
          <p className="text-xs text-zinc-500">Période active : {filters.from_date} → {filters.to_date}{filters.only_late ? ' · retards uniquement' : ''}</p>
        </div>
        <div className="divide-y divide-zinc-100">
          {(grouped.groups || []).map((g) => (
            <div key={g.agent_id || g.agent_name} className="p-4" data-testid={`pilotage-agent-${g.agent_id || 'unassigned'}`}>
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-full bg-emerald-100 text-[#1B4D3E] flex items-center justify-center font-semibold">
                    {(g.agent_name || '?').slice(0, 1).toUpperCase()}
                  </div>
                  <div>
                    <p className="font-medium text-zinc-900">{g.agent_name}</p>
                    <p className="text-xs text-zinc-500">
                      {g.items?.length || 0} action(s) attendue(s)
                    </p>
                  </div>
                </div>
                <Button
                  size="sm"
                  onClick={() => onRelaunchWhatsApp(g)}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white"
                  data-testid={`pilotage-whatsapp-${g.agent_id || 'unassigned'}`}
                  disabled={!g.items?.length}
                >
                  <MessageCircle className="w-3.5 h-3.5 mr-1.5" />
                  Relancer via WhatsApp
                </Button>
              </div>
              <ul className="mt-3 space-y-1.5">
                {(g.items || []).map((it) => (
                  <li key={it.inventory_id} className="text-sm text-zinc-700 flex items-start gap-2">
                    <span className="font-mono text-xs text-zinc-500 whitespace-nowrap mt-0.5">{formatDate(it.date)}</span>
                    <span className="font-medium text-zinc-900">{it.zone_name || '—'}</span>
                    <span className="text-zinc-500">·</span>
                    <span>{it.expected_action}</span>
                    {it.is_late && (
                      <Badge className="bg-red-100 text-red-700 border-red-200 border ml-1 text-xs">
                        Retard {formatDelay(it.delay_minutes)}
                      </Badge>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}
          {!grouped.groups?.length && (
            <div className="px-4 py-6 text-center text-zinc-500 text-sm" data-testid="pilotage-by-agent-empty">
              Aucun agent à relancer sur la période sélectionnée.
            </div>
          )}
        </div>
      </div>

      {/* Conflits de planning */}
      <InventoryConflictsSection />

      {/* Notifications & Devices */}
      <InventoryDevicesAdmin />
    </div>
  );
};

export default InventoryPilotageReport;
